/**
 * classicSettingsDeletes — the classic Configure panel's "a deleted thing comes back" fix (2026-11-03).
 *
 * THE BUG. Classic `PracticePulse.vue` hands the store's LIVE `settings` object to `ConfigurePracticePulse.vue`. Deleting a
 * quick-note category (`delQuickNoteCat`) or a custom room (`delOp`) deletes the map key IN PLACE, then `save()` sends the
 * whole object through the store action `updateOps` → `setDoc(ref, payload, { merge: true })`. A merge never removes a key
 * that is missing from the payload, so the deleted category / room stayed in Firestore and came back on the next load.
 *
 * THE FIX (same idiom as `payload.deletedCheckinKeys`). When the panel opens it snapshots the keys of the two maps it lets the
 * office delete from (`settings.quickNotesOptions2`, `settings.ops`). On save it sends `payload.deletedSettingsKeys`
 * (= the keys that were there at open and are gone now) and `payload.deletedOpColumns` (the removed rooms whose board
 * column is empty). `updateOps` hands the payload to `applyClassicDeletes`, which returns a COPY with `deleteField()` in
 * those places — the live store object is never touched, so a sentinel can never leak into the page.
 *
 * NEVER DROP A COLUMN THAT HOLDS A PATIENT. `settings.ops[<room>]` goes (the room is no longer configured), but the board's own
 * top-level column `ops.<room>` is deleted only when it is missing or `[]` right now (`emptyBoardColumns`); a column with a
 * patient in it is left exactly as it is.
 *
 * ⚠️ Keep this file import-free. The node-tests execute this exact file (cypress/node-tests/classic-settings-deletes.test.js).
 */

/** The two `settings` maps the classic panel lets the office delete keys from. */
export const DELETABLE_MAPS = ['quickNotesOptions2', 'ops'];

const isObj = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);
const keysOf = (v) => (isObj(v) ? Object.keys(v) : []);

/** `{ quickNotesOptions2: [keys], ops: [keys] }` — the keys the two maps hold right now (take this when the panel opens). */
export function snapshotSettingsKeys(settings) {
  const s = isObj(settings) ? settings : {};
  const out = {};
  DELETABLE_MAPS.forEach((m) => { out[m] = keysOf(s[m]); });
  return out;
}

/** The keys present in `snapshot` and gone from `settings` now: `{ quickNotesOptions2: [...], ops: [...] }` (always both arrays). */
export function removedSettingsKeys(snapshot, settings) {
  const snap = isObj(snapshot) ? snapshot : {};
  const s = isObj(settings) ? settings : {};
  const out = {};
  DELETABLE_MAPS.forEach((m) => {
    const now = isObj(s[m]) ? s[m] : {};
    out[m] = (Array.isArray(snap[m]) ? snap[m] : []).filter((k) => !Object.prototype.hasOwnProperty.call(now, k));
  });
  return out;
}

/** True when `removed` holds at least one key to send. */
export function hasRemovedKeys(removed) {
  const r = isObj(removed) ? removed : {};
  return DELETABLE_MAPS.some((m) => Array.isArray(r[m]) && r[m].length > 0);
}

/** Of the removed room keys, those whose column on the board is missing or `[]` — the only columns it is safe to delete. */
export function emptyBoardColumns(roomKeys, boardOps) {
  const ops = isObj(boardOps) ? boardOps : {};
  return (Array.isArray(roomKeys) ? roomKeys : []).filter((k) => {
    const column = ops[k];
    return column === undefined || column === null || (Array.isArray(column) && column.length === 0);
  });
}

/**
 * The rooms to remember as removed (`settings.removedOps`, read by the nightly reset so a room the office deleted is not
 * re-added from Open Dental): `current` + this room's OperatoryNum as a NUMBER, no duplicates, `current` untouched. A custom
 * room (`addedFromTeamio`) is not an Open Dental room, so it is never recorded (returns `current` as it is). Returns a new array
 * whenever it adds, so the caller can Vue.set it.
 */
export function withRemovedOp(current, op) {
  const list = Array.isArray(current) ? current : [];
  if (!isObj(op) || op.addedFromTeamio === true) return list;
  const num = Number(op.OperatoryNum);
  if (!Number.isFinite(num)) return list;
  if (list.some((n) => Number(n) === num)) return list;
  return list.concat(num);
}

/**
 * The payload `updateOps` writes: a shallow COPY of `payload` without the two control fields, with `DEL` (Firestore's
 * `deleteField()` — injected, so this file stays import-free) in place of each removed key. `payload`, `payload.settings`,
 * its two maps and `payload.ops` are never mutated (`settings` is the live store object). A key that is present again in the
 * current map is not deleted.
 *
 * @param {object} payload  { settings?, ops?, deletedSettingsKeys?: {quickNotesOptions2, ops}, deletedOpColumns?: [...], ...rest }
 * @param {*} DEL           the delete marker
 */
export function applyClassicDeletes(payload, DEL) {
  const { deletedSettingsKeys, deletedOpColumns, ...body } = payload;

  if (isObj(deletedSettingsKeys) && isObj(body.settings)) {
    const settings = Object.assign({}, body.settings);
    DELETABLE_MAPS.forEach((m) => {
      const gone = Array.isArray(deletedSettingsKeys[m]) ? deletedSettingsKeys[m] : [];
      if (gone.length === 0) return;
      const map = isObj(settings[m]) ? Object.assign({}, settings[m]) : {};
      gone.forEach((k) => { if (!Object.prototype.hasOwnProperty.call(map, k)) map[k] = DEL; });
      settings[m] = map;
    });
    body.settings = settings;
  }

  if (Array.isArray(deletedOpColumns) && deletedOpColumns.length > 0) {
    const ops = Object.assign({}, isObj(body.ops) ? body.ops : {});
    deletedOpColumns.forEach((k) => { if (!Object.prototype.hasOwnProperty.call(ops, k)) ops[k] = DEL; });
    body.ops = ops;
  }

  return body;
}
